import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Typography, Card, Chip, Avatar } from "@mui/material";
import {
  LocationOn,
  CheckCircle,
  Schedule,
  LocalShipping,
  LocalShipping as TransitIcon,
  Error as ErrorIcon,
  DragIndicator,
} from "@mui/icons-material";
import { useToastStore } from "@/store/toastStore";
import {
  LoadingSpinner,
  EmptyState,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import {
  DeliveriesModeSwitch,
  DeliveriesFilters,
  useDeliveriesFilters,
} from "@/components/deliveries";
import {
  deliveryService,
  Delivery,
  DeliveryStatus,
} from "@/services/delivery.service";
import { zoneService } from "@/services/zone.service";
import { formatCustomerName } from "@/utils/formatCustomerName";

const COLUMN_WIDTH = 320;
const SCROLLBAR = {
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-track": { bgcolor: "action.hover" },
  "&::-webkit-scrollbar-thumb": { borderRadius: 3, bgcolor: "divider" },
};

function parseHourFromTime(t: string): number {
  if (!t || !String(t).trim()) return 0;
  const s = String(t).trim();
  const iso = /^\d{4}-\d{2}-\d{2}T/.test(s) || s.endsWith("Z");
  const d = iso ? new Date(s) : new Date(`1970-01-01T${s}`);
  return d.getHours();
}

function isRushTime(t: string): boolean {
  const h = parseHourFromTime(t);
  return h >= 11 && h < 13;
}

function formatTimeDisplay(
  t: string | undefined | null,
  fallback = "-",
): string {
  if (!t || !String(t).trim()) return fallback;
  const s = String(t).trim();
  if (/^\d{1,2}:\d{2}/.test(s)) return s.slice(0, 5);
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }
  return fallback;
}

function getStatusConfig(
  status: DeliveryStatus,
): {
  label: string;
  color: "success" | "info" | "warning" | "error";
  Icon: typeof CheckCircle;
} {
  switch (status) {
    case "DELIVERED":
      return { label: "Livrée", color: "success", Icon: CheckCircle };
    case "IN_PROGRESS":
      return { label: "En cours", color: "info", Icon: TransitIcon };
    case "FAILED":
      return { label: "Échouée", color: "error", Icon: ErrorIcon };
    default:
      return { label: "Planifiée", color: "warning", Icon: Schedule };
  }
}

interface SortableDeliveryItemProps {
  delivery: Delivery;
  showRush: boolean;
  time: string;
  navigate: (path: string) => void;
  getStatusConfig: (
    status: DeliveryStatus,
  ) => {
    label: string;
    color: "success" | "info" | "warning" | "error";
    Icon: typeof CheckCircle;
  };
}

function SortableDeliveryItem({
  delivery,
  showRush,
  time,
  navigate,
  getStatusConfig,
}: SortableDeliveryItemProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: delivery.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {showRush && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: "1px",
              bgcolor: "primary.main",
              opacity: 0.3,
            }}
          />
          <Chip
            icon={<Schedule sx={{ fontSize: 12 }} />}
            label="11h – 13h Rush"
            size="small"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              textTransform: "uppercase",
              bgcolor: "action.hover",
              color: "primary.main",
              border: "1px solid",
              borderColor: "primary.light",
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
          <Box
            sx={{
              flex: 1,
              height: "1px",
              bgcolor: "primary.main",
              opacity: 0.3,
            }}
          />
        </Box>
      )}
      <Card
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          borderLeft: delivery.status === "PLANNED" ? 4 : 1,
          borderLeftColor:
            delivery.status === "PLANNED" ? "primary.main" : "divider",
          boxShadow: 0,
          "&:hover": { boxShadow: 1 },
          cursor: "pointer",
          display: "flex",
          gap: 1,
        }}
        onClick={() => navigate(`/orders/${delivery.orderId}`)}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "text.secondary",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicator sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              mb: 0.5,
            }}
          >
            <Chip
              icon={
                (() => {
                  const { Icon } = getStatusConfig(delivery.status);
                  return <Icon sx={{ fontSize: 10 }} />;
                })()
              }
              label={getStatusConfig(delivery.status).label}
              size="small"
              color={getStatusConfig(delivery.status).color}
              sx={{
                fontSize: "0.65rem",
                height: 20,
                "& .MuiChip-icon": { mr: 0.25 },
              }}
            />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              fontSize: "0.875rem",
              mb: 0.25,
            }}
          >
            {delivery.order?.customer ? formatCustomerName(delivery.order.customer) : "Client inconnu"}
          </Typography>
          {(delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                mb: 1,
              }}
            >
              <LocationOn sx={{ fontSize: 12 }} />
              <Typography variant="caption" color="text.secondary">
                {delivery.order?.customerAddress?.address ?? delivery.order?.customer?.address ?? "—"}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.65rem",
                color: "text.secondary",
              }}
            >
              #{delivery.order?.orderNumber ?? "-"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {delivery.estimatedTravelTimeMinutes != null && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                  ~{delivery.estimatedTravelTimeMinutes} min
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {formatTimeDisplay(time || undefined, "-")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export function DeliveriesByZonePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { error: showError } = useToastStore();
  const { filters } = useDeliveriesFilters();

  const { data: zones = [], isLoading: zonesLoading } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(),
  });

  const { data: deliveries = [], isLoading: deliveriesLoading } = useQuery({
    queryKey: [
      "deliveries",
      "by-zone",
      filters.status,
      filters.driver,
      filters.zone,
      filters.date,
    ],
    queryFn: () =>
      deliveryService.getAll({
        status: filters.status === "all" ? undefined : filters.status,
        driverId: filters.driver === "all" ? undefined : filters.driver,
        zoneId: filters.zone === "all" ? undefined : filters.zone,
        deliveryDate: filters.date || undefined,
      }),
  });

  const filteredBySearch = useMemo(() => {
    if (!filters.search) return deliveries;
    const q = filters.search.toLowerCase();
    return deliveries.filter(
      (d) =>
        d.order?.orderNumber?.toLowerCase().includes(q) ||
        (d.order?.customer && formatCustomerName(d.order.customer).toLowerCase().includes(q)) ||
        d.order?.customer?.firstName?.toLowerCase().includes(q) ||
        d.order?.customer?.lastName?.toLowerCase().includes(q) ||
        d.driver?.firstName?.toLowerCase().includes(q) ||
        d.driver?.lastName?.toLowerCase().includes(q) ||
        d.zone?.name?.toLowerCase().includes(q),
    );
  }, [deliveries, filters.search]);

  const [localDeliveriesByZone, setLocalDeliveriesByZone] = useState<
    Record<string, Delivery[]>
  >({});

  const deliveriesByZone = useMemo(() => {
    const grouped: Record<string, Delivery[]> = {};
    filteredBySearch.forEach((delivery) => {
      const zoneId = delivery.zoneId || "unassigned";
      if (!grouped[zoneId]) grouped[zoneId] = [];
      grouped[zoneId].push(delivery);
    });
    Object.keys(grouped).forEach((zoneId) => {
      grouped[zoneId].sort((a, b) => {
        if (a.tourOrder !== b.tourOrder) return a.tourOrder - b.tourOrder;
        const ta =
          a.scheduledDeliveryTime ||
          a.order?.deliveryTimeSlot?.split("-")[0]?.trim() ||
          "00:00";
        const tb =
          b.scheduledDeliveryTime ||
          b.order?.deliveryTimeSlot?.split("-")[0]?.trim() ||
          "00:00";
        return ta.localeCompare(tb);
      });
    });
    return grouped;
  }, [filteredBySearch]);

  // Synchroniser avec les données du serveur
  useEffect(() => {
    if (Object.keys(deliveriesByZone).length > 0) {
      setLocalDeliveriesByZone(deliveriesByZone);
    }
  }, [deliveriesByZone]);

  const updateTourOrderMutation = useMutation({
    mutationFn: ({
      deliveryId,
      tourOrder,
    }: { deliveryId: string; tourOrder: number }) =>
      deliveryService.update(deliveryId, { tourOrder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ??
          "Erreur lors de la mise à jour de l'ordre.",
      );
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent, zoneId: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const list = localDeliveriesByZone[zoneId] ?? [];
    const oldIndex = list.findIndex((d) => d.id === active.id);
    const newIndex = list.findIndex((d) => d.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newList = arrayMove(list, oldIndex, newIndex);
    setLocalDeliveriesByZone({
      ...localDeliveriesByZone,
      [zoneId]: newList,
    });

    // Mettre à jour le tourOrder pour chaque livraison affectée
    newList.forEach((delivery, idx) => {
      const newTourOrder = idx + 1;
      if (delivery.tourOrder !== newTourOrder) {
        updateTourOrderMutation.mutate({
          deliveryId: delivery.id,
          tourOrder: newTourOrder,
        });
      }
    });
  };

  if (zonesLoading || deliveriesLoading) {
    return (
      <LoadingSpinner message="Chargement des livraisons par zone..." />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Livraisons par Zone"
        action={<DeliveriesModeSwitch />}
      />

      <FiltersCard>
        <DeliveriesFilters />
      </FiltersCard>

      {zones.length === 0 ? (
        <EmptyState
          title="Aucune zone trouvée"
          description="Créez des zones pour organiser vos livraisons."
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            overflowX: "auto",
            gap: 3,
            pb: 1,
            alignItems: "stretch",
            ...SCROLLBAR,
          }}
        >
          {zones.map((zone) => {
            const list = localDeliveriesByZone[zone.id] || [];
            const deliveryIds = list.map((d) => d.id);
            return (
              <Box
                key={zone.id}
                sx={{
                  flexShrink: 0,
                  width: COLUMN_WIDTH,
                  minWidth: COLUMN_WIDTH,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 1,
                  }}
                >
                  {/* Header — même style que mode livreur */}
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: "divider",
                      background:
                        "linear-gradient(to bottom, white, rgba(0,0,0,0.02))",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          flexShrink: 0,
                          bgcolor: "primary.main",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <LocationOn />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                          }}
                          noWrap
                        >
                          Zone {zone.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {list.length} livraison
                          {list.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={list.length}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        bgcolor: "action.hover",
                        color: "text.secondary",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Box>

                  {/* Liste — même style que mode livreur avec drag and drop */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      minHeight: 200,
                      maxHeight: "calc(100vh - 320px)",
                      p: 1.5,
                      bgcolor: "grey.50",
                      ...SCROLLBAR,
                    }}
                  >
                    {list.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 4,
                          color: "text.secondary",
                        }}
                      >
                        <LocalShipping
                          sx={{
                            fontSize: 40,
                            color: "action.disabled",
                            mb: 1,
                          }}
                        />
                        <Typography variant="body2">
                          Aucune livraison dans cette zone
                        </Typography>
                      </Box>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleDragEnd(e, zone.id)}
                      >
                        <SortableContext
                          items={deliveryIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            {list.map((delivery, idx) => {
                              const time =
                                delivery.scheduledDeliveryTime ||
                                delivery.order?.deliveryTimeSlot
                                  ?.split("-")[0]
                                  ?.trim() ||
                                "";
                              const prevTime =
                                idx > 0
                                  ? list[idx - 1].scheduledDeliveryTime ||
                                    list[idx - 1].order?.deliveryTimeSlot
                                      ?.split("-")[0]
                                      ?.trim() ||
                                    ""
                                  : "";
                              const showRush =
                                isRushTime(time) && !isRushTime(prevTime);

                              return (
                                <SortableDeliveryItem
                                  key={delivery.id}
                                  delivery={delivery}
                                  showRush={showRush}
                                  time={time}
                                  navigate={navigate}
                                  getStatusConfig={getStatusConfig}
                                />
                              );
                            })}
                          </Box>
                        </SortableContext>
                      </DndContext>
                    )}
                  </Box>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
